import React, { useMemo } from 'react';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';
import {
  calculatePositionRowMetrics,
  calculatePortfolioTotalValue,
  formatCurrencyValue,
  normalizeSymbol,
  resolvePositionMarkPrice,
} from '../../../components/trading-dashboard/portfolioValue';
import compactStyles from '../../../components/trading/CompactTable.module.css';
import { SymbolLogo } from '../../../components/common/SymbolLogo';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import { usePositionState } from '../shared/usePositionState';
import { useGroupedPositions } from '../shared/useGroupedPositions';
import { WidgetErrorBoundary } from '../shared/WidgetErrorBoundary';
import { fmtPrice, fmtCurrency, fmtReturnPct, fmtTime, pnlClass } from '../shared/formatters';
import { useState, useCallback } from 'react';

const PositionsInner: React.FC = () => {
  const {
    cash, positions, loading, error,
    latestCloseBySymbol, prevCloseBySymbol, effectiveMarketIsOpen,
  } = usePositionState();
  const { ticksBySymbol, openOrderTicket } = useOrderTicketNavigation();

  const groupedPositions = useGroupedPositions(positions);
  const [expandedSymbols, setExpandedSymbols] = useState<Record<string, boolean>>({});
  const toggleGroup = useCallback(
    (symbol: string) => setExpandedSymbols((prev) => ({ ...prev, [symbol]: !prev[symbol] })),
    [],
  );

  const totalPortfolioValue = useMemo(
    () =>
      calculatePortfolioTotalValue(
        cash, positions, ticksBySymbol, [], latestCloseBySymbol, effectiveMarketIsOpen,
      ),
    [cash, positions, ticksBySymbol, latestCloseBySymbol, effectiveMarketIsOpen],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', color: 'var(--tr-text-primary, #e2e8f0)' }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 10px',
        background: 'var(--tr-bg-secondary, rgba(255,255,255,0.02))',
        borderBottom: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))',
        fontSize: '0.8125rem', fontWeight: 700, gap: '16px',
      }}>
        <span>
          Positions
          {!loading && (
            <span style={{ fontSize: '0.75rem', color: 'var(--tr-text-muted)', marginLeft: '6px' }}>
              ({groupedPositions.length})
            </span>
          )}
        </span>
        <div style={{ display: 'flex', gap: '16px', marginLeft: 'auto' }}>
          <div>
            <span style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--tr-text-muted)', marginRight: '4px' }}>
              Total Portfolio Value
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {loading || error ? '--' : formatCurrencyValue(totalPortfolioValue)}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--tr-text-muted)', marginRight: '4px' }}>
              Cash
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {loading || error ? '--' : formatCurrencyValue(cash)}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', flex: 1 }}>
          Loading…
        </div>
      ) : error ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', color: 'var(--tr-pnl-negative, #ef4444)', fontSize: '0.875rem', flex: 1 }}>
          {error}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <table className={compactStyles.compactTable}>
            <thead>
              <tr>
                <th>Symbol</th>
                <th className={compactStyles.numericCell}>Net Qty</th>
                <th className={compactStyles.numericCell}>Avg Cost Basis</th>
                <th className={compactStyles.numericCell}>Last</th>
                <th className={compactStyles.numericCell}>Previous</th>
                <th className={compactStyles.numericCell}>Total Cost</th>
                <th className={compactStyles.numericCell}>Market Value</th>
                <th className={compactStyles.numericCell}>PnL</th>
                <th className={compactStyles.numericCell}>% Daily Return</th>
                <th className={compactStyles.numericCell}>% Total Return</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {groupedPositions.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', color: 'var(--tr-text-muted)', padding: '16px' }}>
                    No open positions
                  </td>
                </tr>
              )}
              {groupedPositions.map((group) => {
                const pos = group.aggregate;
                const sym = group.symbol;
                const closeQty = Math.abs(pos.net_qty);
                const hasChildren = group.lots.length > 1;
                const expanded = !!expandedSymbols[sym];
                const markPrice = resolvePositionMarkPrice(
                  pos, ticksBySymbol, latestCloseBySymbol, effectiveMarketIsOpen,
                );
                const prevCloseValue = prevCloseBySymbol[sym];
                const previousClose = prevCloseValue?.status === 'ok' ? prevCloseValue.prev_close : null;
                const metrics = calculatePositionRowMetrics({
                  position: pos,
                  resolvedLast: markPrice,
                  previousClose,
                  marketIsOpen: effectiveMarketIsOpen,
                  forceLastEqualsPreviousWhenClosed: true,
                });

                return (
                  <React.Fragment key={sym}>
                    <tr
                      style={hasChildren ? { cursor: 'pointer', background: 'rgba(255,255,255,0.04)' } : undefined}
                      onClick={hasChildren ? () => toggleGroup(sym) : undefined}
                    >
                      <td>
                        <span className={compactStyles.symbolCellContent}>
                          {hasChildren && <span style={{ width: '14px', textAlign: 'center' }}>{expanded ? '▼' : '▶'}</span>}
                          <SymbolLogo symbol={sym} size={16} />
                          <span className={compactStyles.symbolCellLabel}>{sym}</span>
                          {hasChildren && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--tr-text-muted)' }}>
                              {group.lots.length} lots
                            </span>
                          )}
                        </span>
                      </td>
                      <td className={compactStyles.numericCell}>{pos.net_qty}</td>
                      <td className={compactStyles.numericCell}>{fmtPrice(pos.avg_price)}</td>
                      <td className={compactStyles.numericCell}>{fmtPrice(metrics.last)}</td>
                      <td className={compactStyles.numericCell}>{fmtPrice(metrics.previous)}</td>
                      <td className={compactStyles.numericCell}>{fmtCurrency(metrics.totalCost)}</td>
                      <td className={compactStyles.numericCell}>{fmtCurrency(metrics.marketValue)}</td>
                      <td className={`${compactStyles.numericCell} ${pnlClass(metrics.pnl)}`}>{fmtCurrency(metrics.pnl)}</td>
                      <td className={`${compactStyles.numericCell} ${pnlClass(metrics.dailyReturnPct)}`}>{fmtReturnPct(metrics.dailyReturnPct)}</td>
                      <td className={`${compactStyles.numericCell} ${pnlClass(metrics.totalReturnPct)}`}>{fmtReturnPct(metrics.totalReturnPct)}</td>
                      <td>
                        <button
                          type="button"
                          disabled={closeQty <= 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            openOrderTicket({
                              prefill: {
                                symbol: pos.symbol,
                                side: pos.net_qty > 0 ? 'sell' : 'buy',
                                quantity: closeQty,
                              },
                            });
                          }}
                          title="Close this position"
                          aria-label={`Close ${sym} position`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '3px 8px', border: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))',
                            borderRadius: '4px', color: 'var(--tr-text-secondary)', background: 'transparent',
                            fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          Close
                        </button>
                      </td>
                    </tr>
                    {hasChildren && expanded && group.lots.map((lot) => {
                      const lotCloseQty = Math.abs(lot.net_qty);
                      const lotMark = resolvePositionMarkPrice(
                        lot, ticksBySymbol, latestCloseBySymbol, effectiveMarketIsOpen,
                      );
                      const lotMetrics = calculatePositionRowMetrics({
                        position: lot,
                        resolvedLast: lotMark,
                        previousClose,
                        marketIsOpen: effectiveMarketIsOpen,
                        forceLastEqualsPreviousWhenClosed: true,
                      });
                      return (
                        <tr key={lot.lot_id || `${lot.user_uuid}-${lot.symbol}-${lot.trade_at}`} style={{ background: 'rgba(255,255,255,0.01)' }}>
                          <td>
                            <span className={compactStyles.symbolCellContent} style={{ paddingLeft: '26px' }}>
                              <SymbolLogo symbol={sym} size={14} />
                              <span className={compactStyles.symbolCellLabel} style={{ fontWeight: 500 }}>{sym}</span>
                            </span>
                          </td>
                          <td className={compactStyles.numericCell}>{lot.net_qty}</td>
                          <td className={compactStyles.numericCell}>{fmtPrice(lot.avg_price)}</td>
                          <td className={compactStyles.numericCell}>{fmtPrice(lotMetrics.last)}</td>
                          <td className={compactStyles.numericCell}>{fmtPrice(lotMetrics.previous)}</td>
                          <td className={compactStyles.numericCell}>{fmtCurrency(lotMetrics.totalCost)}</td>
                          <td className={compactStyles.numericCell}>{fmtCurrency(lotMetrics.marketValue)}</td>
                          <td className={`${compactStyles.numericCell} ${pnlClass(lotMetrics.pnl)}`}>{fmtCurrency(lotMetrics.pnl)}</td>
                          <td className={`${compactStyles.numericCell} ${pnlClass(lotMetrics.dailyReturnPct)}`}>{fmtReturnPct(lotMetrics.dailyReturnPct)}</td>
                          <td className={`${compactStyles.numericCell} ${pnlClass(lotMetrics.totalReturnPct)}`}>{fmtReturnPct(lotMetrics.totalReturnPct)}</td>
                          <td>
                            <button
                              type="button"
                              disabled={lotCloseQty <= 0}
                              onClick={() => openOrderTicket({
                                prefill: {
                                  symbol: sym,
                                  side: lot.net_qty > 0 ? 'sell' : 'buy',
                                  quantity: lotCloseQty,
                                },
                              })}
                              title="Close this lot"
                              aria-label={`Close ${sym} lot`}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                padding: '3px 8px', border: '1px solid var(--tr-row-separator, rgba(255,255,255,0.06))',
                                borderRadius: '4px', color: 'var(--tr-text-secondary)', background: 'transparent',
                                fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                            >
                              Close
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const PositionsWidget: React.FC<WidgetInstanceProps> = (_props) => (
  <WidgetErrorBoundary name="Positions">
    <PositionsInner />
  </WidgetErrorBoundary>
);

export default PositionsWidget;
